import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { gettext } from "web/script/modules/django-i18n";
import { UniversalFilterOption } from "web/types/universal-filters";
import * as styles from "./gender-tabs.css";

export const GenderMapping = {
    men: gettext("header.gender.men"), // Men
    women: gettext("header.gender.women"), // Women
};

export function getGenderTabId(optionId: string): string {
    return `gender-tab-${optionId}`;
}

interface GenderTabsProps {
    options: UniversalFilterOption[];
    onSelect: (option: UniversalFilterOption) => void;
    panelId: string;
}

export function GenderTabs({ options, onSelect, panelId }: GenderTabsProps): JSX.Element {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const selectedIndex = options.findIndex((option) => !!option.checked);
    const [focusedIndex, setFocusedIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

    useEffect(() => {
        if (selectedIndex >= 0) {
            setFocusedIndex(selectedIndex);
        }
    }, [selectedIndex]);

    function moveFocus(index: number): void {
        const next = (index + options.length) % options.length;

        setFocusedIndex(next);
        tabRefs.current[next]?.focus();
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number): void {
        switch (event.key) {
            case "ArrowRight":
                moveFocus(index + 1);
                break;
            case "ArrowLeft":
                moveFocus(index - 1);
                break;
            case "Home":
                moveFocus(0);
                break;
            case "End":
                moveFocus(options.length - 1);
                break;
            default:
                return;
        }

        event.preventDefault();
    }

    return (
        <div
            className={styles.tabList}
            role="tablist"
            aria-label={
                // Gender
                gettext("feed.filters.gender_title")
            }
        >
            {options.map((option, index) => {
                const isSelected = !!option.checked;
                const tabId = getGenderTabId(option.id ?? String(option.value));

                return (
                    <button
                        key={tabId}
                        ref={(element) => (tabRefs.current[index] = element)}
                        type="button"
                        role="tab"
                        id={tabId}
                        aria-selected={isSelected}
                        aria-controls={panelId}
                        tabIndex={index === focusedIndex ? 0 : -1}
                        className={clsx({
                            [styles.tab]: !isSelected,
                            [styles.selectedTab]: isSelected,
                        })}
                        onClick={() => onSelect(option)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                    >
                        <p className={styles.label}>
                            {GenderMapping[option.value.toString().toLowerCase()]}
                        </p>
                    </button>
                );
            })}
        </div>
    );
}
